import { useState } from 'react'
import { useCrosswordStore } from '../store'
import { getWordsInGroup, getLetters, getLettersFromText } from '../lib/pairings'
import { suggestInflectedForms } from '../lib/constructor'
import { CrosswordWord } from './CrosswordText'
import { TagEditor } from './TagEditor'
import type { Word } from '../types'

type WordVariantSuggestion = {
  text: string
  sourceWordText: string
  // Same-list duplicate of this inflection — can be grouped with selected.
  existingSameListWord: Word | null
  // Other-list duplicate (different role, same letters) — can be marked as twin.
  existingOtherListWord: Word | null
}

type WordAssistantBodyProps = {
  onClose: () => void
}

/**
 * AssistantPanel body for the Words page. Shows the selected word's group
 * (with edit / delete / add-variant), plus inflection-based variant
 * suggestions with "Add", "Group", or "Mark twin" actions depending on
 * whether the candidate already exists in another list / list slot.
 */
export function WordAssistantBody({ onClose }: WordAssistantBodyProps) {
  const {
    words,
    selectedWordId,
    setSelectedWord,
    updateWord,
    deleteWord,
    addVariant,
    createWordGroup,
    markAsTwins,
    setWordTags,
  } = useCrosswordStore()

  const [editingWordId, setEditingWordId] = useState<string | null>(null)
  const [editText, setEditText] = useState('')
  const [addingVariant, setAddingVariant] = useState(false)
  const [variantText, setVariantText] = useState('')

  const selectedWord = selectedWordId ? words.find(w => w.id === selectedWordId) ?? null : null

  if (!selectedWord) {
    return (
      <div className="px-4 py-8 text-center text-sm text-gray-400">
        Select a word to see variant suggestions and grouping options.
      </div>
    )
  }

  const groupWords = getWordsInGroup(selectedWord, words)

  // Inflection-based variant suggestions. Cross-references existing words by
  // (list, letters) so we can offer Group / Mark-twin instead of Add when the
  // inflected form is already in the corpus.
  const variantSuggestions: WordVariantSuggestion[] = (() => {
    const selectedGroupLetters = new Set(groupWords.map(word => getLetters(word)))
    const wordsByListAndLetters = new Map<string, Word>()
    for (const word of words) {
      const key = `${word.list ?? 'unsorted'}:${getLetters(word)}`
      wordsByListAndLetters.set(key, word)
    }

    const selectedListKey = selectedWord.list ?? 'unsorted'
    const otherListKey: string = selectedWord.list === 'A' ? 'B' : selectedWord.list === 'B' ? 'A' : 'unsorted'

    const suggestionsByLetters = new Map<string, WordVariantSuggestion>()
    for (const sourceWord of groupWords) {
      for (const inflectedText of suggestInflectedForms(sourceWord.text)) {
        const letters = getLettersFromText(inflectedText)
        if (selectedGroupLetters.has(letters) || suggestionsByLetters.has(letters)) continue

        const existingSameListWord = wordsByListAndLetters.get(`${selectedListKey}:${letters}`) ?? null
        const existingOtherListWord = otherListKey === selectedListKey
          ? null
          : wordsByListAndLetters.get(`${otherListKey}:${letters}`) ?? null

        suggestionsByLetters.set(letters, {
          text: inflectedText,
          sourceWordText: sourceWord.text,
          existingSameListWord,
          existingOtherListWord,
        })
      }
    }

    return Array.from(suggestionsByLetters.values()).sort((a, b) => {
      const lengthDiff = b.text.length - a.text.length
      if (lengthDiff !== 0) return lengthDiff
      return a.text.localeCompare(b.text)
    })
  })()

  const getGroupWordIds = (word: Word): string[] => {
    if (!word.groupId) return [word.id]
    return words.filter(candidate => candidate.groupId === word.groupId).map(candidate => candidate.id)
  }

  const handleGroupVariant = (word: Word) => {
    const mergedIds = new Set<string>(getGroupWordIds(selectedWord))
    for (const wordId of getGroupWordIds(word)) mergedIds.add(wordId)
    if (mergedIds.size >= 2) createWordGroup(Array.from(mergedIds))
  }

  const handleSaveEdit = (wordId: string) => {
    if (editText.trim()) updateWord(wordId, { text: editText.trim() })
    setEditingWordId(null)
    setEditText('')
  }

  return (
    <div className="flex flex-col h-full">
      {/* Pinned word card */}
      <div className="p-4 border-b border-gray-100 shrink-0">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
            {groupWords.length > 1 ? 'Word group' : 'Selected word'}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 tabular-nums">
              {getLetters(selectedWord).length} letters
            </span>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              title="Close assistant"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="space-y-1.5">
          {groupWords.map(word => {
            const isEditing = editingWordId === word.id
            const isPrimary = word.id === selectedWord.id
            return (
              <div key={word.id} className="flex items-center gap-2 group min-w-0">
                {isEditing ? (
                  <input
                    type="text"
                    value={editText}
                    onChange={e => setEditText(e.target.value)}
                    onBlur={() => handleSaveEdit(word.id)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleSaveEdit(word.id)
                      if (e.key === 'Escape') { setEditingWordId(null); setEditText('') }
                    }}
                    className="px-2 py-1 border border-gray-300 rounded text-base font-mono bg-white"
                    autoFocus
                  />
                ) : (
                  <button
                    onClick={() => {
                      setSelectedWord(word.id)
                      setEditingWordId(word.id)
                      setEditText(word.text)
                    }}
                    className={`max-w-full overflow-x-auto hover:opacity-70 transition-opacity ${isPrimary ? '' : 'opacity-50'}`}
                  >
                    <CrosswordWord text={word.text} />
                  </button>
                )}
                <button
                  onClick={() => {
                    if (confirm(`Delete "${word.text}"?`)) {
                      deleteWord(word.id)
                      if (word.id === selectedWord.id) {
                        const remaining = groupWords.filter(w => w.id !== word.id)
                        setSelectedWord(remaining[0]?.id ?? null)
                      }
                    }
                  }}
                  className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition-all text-sm"
                  title="Delete"
                >
                  ✕
                </button>
              </div>
            )
          })}

          {addingVariant ? (
            <input
              type="text"
              value={variantText}
              onChange={e => setVariantText(e.target.value)}
              onBlur={() => {
                if (variantText.trim()) addVariant(selectedWord.id, variantText.trim())
                setVariantText('')
                setAddingVariant(false)
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' && variantText.trim()) {
                  addVariant(selectedWord.id, variantText.trim())
                  setVariantText('')
                  setAddingVariant(false)
                }
                if (e.key === 'Escape') { setAddingVariant(false); setVariantText('') }
              }}
              placeholder="New variant..."
              className="px-2 py-1 border border-gray-300 rounded text-base font-mono bg-white"
              autoFocus
            />
          ) : (
            <button
              onClick={() => setAddingVariant(true)}
              className="px-2 py-1 border border-gray-200 border-dashed rounded text-xs text-gray-400 hover:text-gray-600 hover:border-gray-400 transition-colors"
              title="Add variant"
            >
              + Variant
            </button>
          )}
        </div>

        {/* Tags — apply to the selected word only (not the whole group). */}
        <div className="mt-3">
          <div className="text-[11px] uppercase tracking-wider text-gray-400 mb-1">Tags</div>
          <TagEditor
            tags={selectedWord.tags ?? []}
            onChange={(tags) => setWordTags(selectedWord.id, tags)}
          />
        </div>
      </div>

      {/* Variant suggestions */}
      <div className="flex-1 overflow-y-auto p-3">
        {variantSuggestions.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-6">
            No suggested variants for this word.
          </p>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2 px-1">
              <span className="text-[11px] font-medium uppercase tracking-wider text-gray-400">
                Suggested variants
              </span>
              <span className="text-[11px] text-gray-400 tabular-nums">
                {variantSuggestions.length}
              </span>
            </div>
            <div className="space-y-1.5">
              {variantSuggestions.map(suggestion => {
                const sameList = suggestion.existingSameListWord
                const otherList = suggestion.existingOtherListWord
                const subtitle = sameList
                  ? `already in List ${sameList.list ?? '—'}`
                  : otherList
                    ? `also in List ${otherList.list ?? '—'}`
                    : `from ${suggestion.sourceWordText}`
                return (
                  <div
                    key={suggestion.text}
                    className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md border border-gray-100 bg-gray-50/40"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-mono leading-none text-gray-700 truncate">
                        {suggestion.text}
                      </p>
                      <p className="text-[11px] text-gray-400 truncate mt-1">{subtitle}</p>
                    </div>
                    {sameList ? (
                      <button
                        onClick={() => handleGroupVariant(sameList)}
                        title="Group with selected word"
                        className="px-2 py-1 text-[11px] font-medium rounded transition-colors bg-gray-200 text-gray-700 hover:bg-gray-300 shrink-0"
                      >
                        Group
                      </button>
                    ) : otherList ? (
                      <button
                        onClick={() => markAsTwins(selectedWord.id, otherList.id)}
                        title="Mark as duplicate across lists"
                        className="px-2 py-1 text-[11px] font-medium rounded transition-colors bg-gray-200 text-gray-700 hover:bg-gray-300 shrink-0"
                      >
                        Mark twin
                      </button>
                    ) : (
                      <button
                        onClick={() => addVariant(selectedWord.id, suggestion.text)}
                        className="px-2 py-1 text-[11px] font-medium rounded bg-gray-800 text-white hover:bg-gray-700 transition-colors shrink-0"
                      >
                        Add
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
